import React, { useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "@/lib/toast";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { CheckCircle2, ShieldCheck } from "lucide-react";

const REASONS = [
  { id: "sold_on_platform", label: "Sold here on OpticSwap", status: "sold", hint: "The buyer paid through OpticSwap's protected checkout." },
  { id: "sold_elsewhere", label: "Sold elsewhere / off-platform", status: "sold", hint: "You completed this sale outside OpticSwap." },
  { id: "no_longer_for_sale", label: "No longer for sale", status: "removed", hint: "Keeping the item, or listed by mistake." },
  { id: "other", label: "Other reason", status: "removed", hint: "" },
];

export function CloseListingButton({ listingId, onDone, variant = "full", listingTitle }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const chosen = REASONS.find((r) => r.id === reason);
  const offPlatform = reason === "sold_elsewhere";

  const submit = async () => {
    if (!reason) { toast.error("Pick a reason so we close this correctly."); return; }
    setBusy(true);
    try {
      // The close ALWAYS goes through — we never hard-block a cancellation.
      await api.post(`/listings/${listingId}/status?status=${chosen.status}`, { close_reason: reason, close_note: note });
      toast.success(chosen.status === "sold" ? "Listing closed and marked sold." : "Listing closed.");
      setOpen(false); setReason(""); setNote("");
      onDone && onDone();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {variant === "icon" ? (
          <button title="Close listing" data-testid={`close-listing-icon-${listingId}`}
            className="p-2 text-muted-foreground hover:text-blaze transition-colors"><CheckCircle2 className="w-4 h-4" /></button>
        ) : (
          <button data-testid="sold-btn"
            className="w-full flex items-center justify-center gap-2 border border-blaze/50 text-blaze hover:bg-blaze/10 font-mono uppercase text-sm py-2.5 transition-colors"><CheckCircle2 className="w-4 h-4" /> Close / Mark Sold</button>
        )}
      </DialogTrigger>
      <DialogContent className="bg-surface border-sniper text-foreground rounded-none max-w-lg" data-testid="close-listing-dialog">
        <DialogHeader><DialogTitle className="font-heading uppercase">Close this listing</DialogTitle></DialogHeader>
        <DialogDescription className="text-xs font-mono uppercase text-muted-foreground">{listingTitle ? `Re: ${listingTitle}` : "Tell us why so we close it correctly."}</DialogDescription>
        <div className="space-y-2" data-testid="close-reason-options">
          {REASONS.map((r) => (
            <button key={r.id} type="button" onClick={() => setReason(r.id)} data-testid={`close-reason-${r.id}`}
              className={`w-full text-left border px-3 py-2.5 transition-colors ${reason === r.id ? "border-olive-bright bg-olive/10" : "border-sniper hover:border-olive"}`}>
              <span className="font-heading text-sm uppercase text-foreground">{r.label}</span>
              {r.hint && <span className="block text-[11px] font-body text-muted-foreground mt-0.5">{r.hint}</span>}
            </button>
          ))}
        </div>
        {offPlatform && (
          <div className="border border-volt/40 bg-volt/5 px-3 py-3" data-testid="close-carrot">
            <p className="font-mono text-[10px] uppercase text-volt flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Before you close off-platform</p>
            <ul className="mt-1.5 space-y-1 text-[12px] font-body text-secondary-foreground list-disc pl-4">
              <li>Guaranteed payout — no meeting a stranger with cash.</li>
              <li>No chargeback exposure once the payment releases.</li>
              <li>Feedback that builds your reputation and lowers your fee tier.</li>
              <li>Buyer protection a private sale can't offer.</li>
            </ul>
            <p className="mt-2 text-[11px] font-body text-muted-foreground">Publishing committed you to completing this sale on OpticSwap while it was active — selling off-platform is a violation of the seller terms.</p>
          </div>
        )}
        <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="close-note-input"
          placeholder="Add a note (optional)…" className="w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2.5 text-foreground transition-colors" />
        <DialogFooter>
          <button type="button" onClick={() => { setOpen(false); setReason(""); setNote(""); }} disabled={busy} data-testid="close-cancel-btn"
            className="border border-sniper hover:border-olive text-secondary-foreground hover:text-foreground font-mono uppercase text-sm px-6 py-2.5 transition-colors disabled:opacity-50">Cancel</button>
          <button onClick={submit} disabled={busy || !reason} data-testid="close-confirm-btn"
            className="bg-blaze hover:brightness-110 text-black font-heading font-bold uppercase px-6 py-2.5 transition-colors disabled:opacity-50">{busy ? "Closing…" : (chosen?.status === "sold" ? "Mark as sold" : "Close listing")}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
